import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { NavigationBlockProvider } from '@/shared/contexts/NavigationBlockContext';

// Lazy load page components
const DashboardPage = React.lazy(() => import('../../features/Dashboard'));
const DashboardSettingsPage = React.lazy(() => import('../../features/Dashboard/pages/DashboardSettings'));
const PatientMasterPage = React.lazy(() => import('../../features/PatientMaster'));
const CcdaViewerPage = React.lazy(() => import('../../features/PatientMaster/pages/CcdaViewerPage'));
const CcdaViewerPageCustom = React.lazy(() => import('../../features/PatientMaster/pages/CcdaViewerPageCustom'));
const PatientDashboardPage = React.lazy(() => import('../../features/PatientDashboard'));
const AppointmentsPage = React.lazy(() => import('../../features/Appointments'));
const FormsPage = React.lazy(() => import('../../features/Forms/pages/Forms'));
const SaveFormPage = React.lazy(() => import('../../features/Forms/pages/SaveForm'));
const TemplatesPage = React.lazy(() => import('../../features/Templates'));
const TemplatesNewPage = React.lazy(() => import('../../features/Templates/pages/CreateTemplate'));
const RemainderPage = React.lazy(() => import('../../features/Reminders'));
const AddNewReminderPage = React.lazy(() => import('../../features/Reminders/pages/AddNewReminder'));
const PatientGroupsPage = React.lazy(() => import('../../features/Settings/pages/patient-groups/PatientGroups'));
const CommunicationPage = React.lazy(() => import('../../features/Communication'));
const HealthRecordsPage = React.lazy(() => import('../../features/HealthRecords'));
const PaymentTransactionsPage = React.lazy(() => import('../../features/PaymentTransactions'));
const SettingsPage = React.lazy(() => import('../../features/Settings'));
const NotificationsPage = React.lazy(() => import('../../features/Notifications'));
const NotFoundPage = React.lazy(() => import('@/shared/components/not-found-page'));
const Login = React.lazy(() => import('@/features/Auth/pages/AuthLogin'));

import PrivateRoute from './privateRoutes';
import PublicRoute from './publicRoutes';
import MainLayout from '@/app/layout/MainLayout';

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
  </div>
);


const AppRoutes = () => {
  return (
    <Router>
      <NavigationBlockProvider>
      <Routes>
        {/* Public route (redirects if already logged in) */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <Login />
              </Suspense>
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route element={<PrivateRoute />}>
          <Route path="/dashboard" element={
            <MainLayout title="Dashboard">
              <Suspense fallback={<LoadingSpinner />}>
                <DashboardPage />
              </Suspense>
            </MainLayout>
            
          } />
          <Route path="/dashboard/settings" element={
            <MainLayout>
              <Suspense fallback={<LoadingSpinner />}>
                <DashboardSettingsPage />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/patient-master" element={
            <MainLayout title="Manage Invites">
              <Suspense fallback={<LoadingSpinner />}>
                <PatientMasterPage />
              </Suspense>
            </MainLayout>
          } />
          {/* Parent tab route - must come before :patientId route */}
          <Route path="/patient-master/parent" element={
            <MainLayout title="Onboarding">
              <Suspense fallback={<LoadingSpinner />}>
                <PatientMasterPage />
              </Suspense>
            </MainLayout>
          } />
          {/* CCDA custom route - must come before :patientId route */}
          <Route path="/patient-master/ccda-custom/:ccdaId" element={
            <MainLayout title="Clinical Summary (Custom)">
              <Suspense fallback={<LoadingSpinner />}>
                <CcdaViewerPageCustom />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/patient-master/:patientId" element={
            <MainLayout title="Onboarding">
              <Suspense fallback={<LoadingSpinner />}>
                <PatientMasterPage />
              </Suspense>
            </MainLayout>
          } />
          {/* <Route path="/patient-dashboard" element={
            <MainLayout title="Patient Dashboard">
              <Suspense fallback={<LoadingSpinner />}>
                <PatientDashboardPage />
              </Suspense>
            </MainLayout>
          } /> */}
          {/* <Route path="/patient-dashboard/:patientId" element={
            <MainLayout title="Patient Dashboard">
              <Suspense fallback={<LoadingSpinner />}>
                <PatientDashboardPage />
              </Suspense>
            </MainLayout>
          } /> */}
          <Route path="/patient-dashboard/ccda-custom/:ccdaId" element={
            <MainLayout title="Clinical Summary (Custom)">
              <Suspense fallback={<LoadingSpinner />}>
                <CcdaViewerPageCustom />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/appointments" element={
            <MainLayout title="Appointments">
              <Suspense fallback={<LoadingSpinner />}>
                <AppointmentsPage />
              </Suspense>
            </MainLayout>
          } />
          {/* Parent tab route - must come before :patientId route */}
          <Route path="/appointments/parent" element={
            <MainLayout title="Appointments">
              <Suspense fallback={<LoadingSpinner />}>
                <AppointmentsPage />
              </Suspense>
            </MainLayout>
          } />
          {/* CCDA custom route - must come before :patientId route */}
          <Route path="/appointments/ccda-custom/:ccdaId" element={
            <MainLayout title="Clinical Summary (Custom)">
              <Suspense fallback={<LoadingSpinner />}>
                <CcdaViewerPageCustom />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/appointments/:patientId" element={
            <MainLayout title="Appointments">
              <Suspense fallback={<LoadingSpinner />}>
                <AppointmentsPage />
              </Suspense>
            </MainLayout>
          } />
          {/* Redirect /configurations to /forms by default */}
          <Route path="/configurations" element={<Navigate to="/configurations/forms" replace />} />
          <Route path="/configurations/forms" element={
            <MainLayout title="Forms Library">
              <Suspense fallback={<LoadingSpinner />}>
                <FormsPage />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/configurations/forms/builder" element={
            <MainLayout title="Form Builder">
              <Suspense fallback={<LoadingSpinner />}>
                <SaveFormPage />
              </Suspense>
            </MainLayout>
          }/>
          <Route path="/configurations/forms/builder/:id" element={
            <MainLayout title="Form Builder">
              <Suspense fallback={<LoadingSpinner />}>
                <SaveFormPage />
              </Suspense>
            </MainLayout>
          }/>
          <Route path="/configurations/templates" element={
            <MainLayout title="Communication Templates">
              <Suspense fallback={<LoadingSpinner />}>
                <TemplatesPage />
              </Suspense>
            </MainLayout>
          } />
          <Route 
            path="/configurations/templates/new" 
            element={
              <MainLayout title="Communication Templates">
                <Suspense fallback={<LoadingSpinner />}>
                  <TemplatesNewPage />
                </Suspense>
              </MainLayout>
            } 
          />
          <Route 
            path="/configurations/templates/edit/:id" 
            element={
              <MainLayout title="Communication Templates">
                <Suspense fallback={<LoadingSpinner />}>
                  <TemplatesNewPage />
                </Suspense>
              </MainLayout>
            } 
          />
          <Route path="/configurations/reminders" element={
            <MainLayout title="Reminder List" collapsSidebar={true}>
              <Suspense fallback={<LoadingSpinner />}>
                <RemainderPage />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/configurations/reminders/new" element={
            <MainLayout title="Add New Reminder">
              <Suspense fallback={<LoadingSpinner />}>
                <AddNewReminderPage />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/configurations/reminders/edit/:id" element={
            <MainLayout title="Edit Reminder">
              <Suspense fallback={<LoadingSpinner />}>
                <AddNewReminderPage />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/configurations/patient-groups" element={
            <MainLayout title="Patient Groups">
              <Suspense fallback={<LoadingSpinner />}>
                <PatientGroupsPage />
              </Suspense>
            </MainLayout>
          } />
          {/* Redirect /communication to /communication/messages by default */}
          <Route path="/communication" element={<Navigate to="/communication/messages" replace />} />
          <Route path="/communication/:tab" element={
            <MainLayout title="" collapsSidebar={true}>
              <Suspense fallback={<LoadingSpinner />}>
                <CommunicationPage />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/health-records" element={
            <MainLayout title="">
              <Suspense fallback={<LoadingSpinner />}>
                <HealthRecordsPage />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/health-records/:patientId" element={
            <MainLayout title="">
              <Suspense fallback={<LoadingSpinner />}>
                <HealthRecordsPage />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/health-records/ccda-custom/:ccdaId" element={
            <MainLayout title="Clinical Summary (Custom)">
              <Suspense fallback={<LoadingSpinner />}>
                <CcdaViewerPageCustom />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/payment-transactions" element={
            <MainLayout title="Payment Transactions">
              <Suspense fallback={<LoadingSpinner />}>
                <PaymentTransactionsPage />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
          <Route path="/settings/:section" element={
            <MainLayout title="Settings">
              <Suspense fallback={<LoadingSpinner />}>
                <SettingsPage />
              </Suspense>
            </MainLayout>
          } />
          <Route path="/notifications" element={
            <MainLayout title="">
              <Suspense fallback={<LoadingSpinner />}>
                <NotificationsPage />
              </Suspense>
            </MainLayout>
          } />
          {/* Redirect to dashboard if no path is provided */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Route>

        {/* 👇 Catch-all route for undefined paths */}
        <Route path="*" element={
          <Suspense fallback={<LoadingSpinner />}>
            <NotFoundPage />
          </Suspense>
        } />
      </Routes>
      </NavigationBlockProvider>
    </Router>
  );
};

export default AppRoutes;